import { FC, useMemo, useState } from 'react'
import classnames from 'classnames'

import { Button } from '../../../../../../@paco/components'
import { EditTimeButton } from '../../../../../../@paco/compositions'
import { ConnectedEditBaseScheduleShiftTimeForm, ConnectedEditShiftConceptTimeForm } from '../../../../../../@paco/connectors'
import { transformToPeriod } from '../../../../../../@paco/entities/Period/PeriodTransformers'
import trans from '../../../../../../@paco/helpers/trans'

import { UnavailableToWorkTimeSlotViewModel } from '../../../../../../entities/UnavailableToWorkTimeSlot/UnavailableToWorkTimeSlot'
import {
    AbsenceViewModel,
    LeaveOfAbsenceViewModel,
    ShiftDraggable,
    ShiftType,
    UserViewModel,
} from '../../../../../../models'
import RegisteredUsers from '../RegisteredUsers/RegisteredUsers'
import ShiftPlanning from '../ShiftPlanning/ShiftPlanning'
import ShiftPreviouslyPlannedUser from '../ShiftPreviouslyPlannedUser/ShiftPreviouslyPlannedUser'
import ShiftTemporaryWorker from '../ShiftTemporaryWorker/ShiftTemporaryWorker'

import './ConceptShift.scss'

interface ConceptShiftProps {
    shift: ShiftDraggable;
    leaveOfAbsences?: LeaveOfAbsenceViewModel[];
    absences?: AbsenceViewModel[];
    unavailableTimeSlots?: UnavailableToWorkTimeSlotViewModel[];
    usersWithOverlappingShift?: UserViewModel[];
    canEditShifts: boolean;
    canEditShiftConcepts: boolean;
    canEditBaseScheduleShifts: boolean;
    showEmploymentTypeBadge: boolean;
    showOverlappingShiftsWarnings: boolean;
    isHighlighted: boolean;
    onShiftClick?: (shift: ShiftDraggable) => void;
    onShiftStartDrag?: (shift: ShiftDraggable) => void;
    className?: string;
}

const ConceptShift: FC<ConceptShiftProps> = ({
    shift,
    leaveOfAbsences = [],
    absences = [],
    unavailableTimeSlots = [],
    usersWithOverlappingShift = [],
    canEditShifts,
    canEditShiftConcepts,
    canEditBaseScheduleShifts,
    showEmploymentTypeBadge,
    showOverlappingShiftsWarnings,
    isHighlighted,
    onShiftClick,
    onShiftStartDrag,
    className = '',
}) => {
    const [showEditTimeForm, setShowEditTimeForm] = useState(false)

    const isShift = shift.type === ShiftType.shift
    const isConcept = shift.type === ShiftType.concept
    const isBaseSchedule = shift.type === ShiftType.baseSchedule
    const isDisabled = isShift || (isConcept && ! canEditShiftConcepts) || (isBaseSchedule && ! canEditBaseScheduleShifts)

    const classNames = useMemo(() => classnames('concept-shift', {
        [`${className}--is-concept`]: isConcept,
        'concept-shift--is-base-schedule': isBaseSchedule,
        [`concept-shift--is-${shift.status}-status`]: shift.status,
        [`concept-shift--is-${shift.dragState}`]: shift.dragState,
        'concept-shift--is-highlighted': isHighlighted,
        'concept-shift--is-concept': isConcept,
    }, [className]), [
        shift,
        className,
        canEditShifts,
        canEditShiftConcepts,
    ])

    const shiftPlanningClassName = 'concept-shift__shift-planning'
    const employmentTypeClassName = 'concept-shift__employment-type'

    const { shiftPlannings, temporaryWorkers, previouslyPlannedUsers } = shift

    const period = useMemo(() => transformToPeriod(shift.start, shift.end.transformToUpToButExcludingDate().date), [shift])
    const plannedShiftPlannings = useMemo(() => shiftPlannings
        .filter(shiftPlanning => shiftPlanning.planned), [shift])
    const unplannedShiftPlannings = useMemo(() => shiftPlannings
        .filter(shiftPlanning => ! shiftPlanning.planned), [shiftPlannings])

    const handleShiftClick = (): void => {
        if (! isDisabled && onShiftClick) {
            onShiftClick(shift)
        }
    }

    const handleDragStart = (): void => {
        if (! isDisabled && onShiftStartDrag) {
            onShiftStartDrag(shift)
        }
    }

    const handleEditShiftConceptTimeFormClose = (): void => {
        setShowEditTimeForm(false)
    }

    const handleTimeButtonClick = (): void => {
        setShowEditTimeForm(true)
    }

    return (
        <div
            id={`concept-shift-${shift.id}${shift.dragState === 'preview' ? '-preview' : ''}`}
            draggable={! isDisabled && !! onShiftStartDrag}
            onDragStart={handleDragStart}
            className={classNames}
        >
            <div className="concept-shift__inner">
                <div className="concept-shift__time-and-people-count concept-shift__top">
                    {showEditTimeForm && isConcept && (
                        <ConnectedEditShiftConceptTimeForm
                            period={period}
                            shiftConceptId={shift.id}
                            onClose={handleEditShiftConceptTimeFormClose}
                            className="concept-shift__edit-time-form"
                        />
                    )}
                    {showEditTimeForm && isBaseSchedule && (
                        <ConnectedEditBaseScheduleShiftTimeForm
                            period={period}
                            baseScheduleShiftId={shift.id}
                            onClose={handleEditShiftConceptTimeFormClose}
                            className="concept-shift__edit-time-form"
                        />
                    )}
                    {! showEditTimeForm && (
                        <EditTimeButton
                            isDisabled={isDisabled}
                            period={period}
                            onClick={handleTimeButtonClick}
                            className="concept-shift__edit-time-button"
                        />
                    )}
                    {`${plannedShiftPlannings.length + temporaryWorkers.length}/${shift.peopleLimit}`}
                </div>
                {shift.department && (
                    <div className="concept-shift__department">
                        {shift.department.group && (
                            <span className="concept-shift__department-group">
                                {`${shift.department.group.name} - `}
                            </span>
                        )}
                        {shift.department.name}
                    </div>
                )}
                { !! (plannedShiftPlannings.length || temporaryWorkers.length) && (
                    <ul className="concept-shift__employees">
                        {plannedShiftPlannings
                            .map(shiftPlanning => (
                                <ShiftPlanning
                                    key={shiftPlanning.id}
                                    showEmploymentTypeBadge={showEmploymentTypeBadge}
                                    shift={shift}
                                    shiftPlanning={shiftPlanning}
                                    absences={absences}
                                    leaveOfAbsences={leaveOfAbsences}
                                    unavailableTimeSlots={unavailableTimeSlots}
                                    overlapsWithOtherShifts={
                                        showOverlappingShiftsWarnings
                                            ? usersWithOverlappingShift.some((user) => user === shiftPlanning.user)
                                            : false
                                    }
                                    className={shiftPlanningClassName}
                                    employmentTypeClassName={employmentTypeClassName}
                                    timeClassName="concept-shift__employee-time"
                                />
                            ))}
                        {temporaryWorkers
                            .map((temporaryWorker) => (
                                <ShiftTemporaryWorker
                                    key={temporaryWorker.id}
                                    className={shiftPlanningClassName}
                                    employmentTypeClassName={employmentTypeClassName}
                                    showEmploymentTypeBadge={showEmploymentTypeBadge}
                                    shift={shift}
                                    temporaryWorker={temporaryWorker}
                                />
                            ))}
                    </ul>
                )}
                { !! previouslyPlannedUsers.length && (
                    <ul className="concept-shift__employees concept-shift__employees--previously-planned">
                        {previouslyPlannedUsers
                            .map((user) => (
                                <ShiftPreviouslyPlannedUser
                                    key={user.id}
                                    className={shiftPlanningClassName}
                                    employmentTypeClassName={employmentTypeClassName}
                                    employmentTypeSlug={user.employmentType?.slug}
                                    showEmploymentTypeBadge={showEmploymentTypeBadge}
                                    user={user}
                                />
                            ))}
                    </ul>
                )}
            </div>
            { !! unplannedShiftPlannings.length && (
                <RegisteredUsers
                    className="concept-shift__registered-users"
                    length={unplannedShiftPlannings.length}
                />
            )}

            <Button
                hideLabel
                disabled={isDisabled}
                text={trans('common.shift')}
                onClick={handleShiftClick}
                className="concept-shift__button"
            />
        </div>
    )
}

export default ConceptShift
